import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import axios from '../utils/api';

const initialForm = {
  template_name: '',
  channel: 'email',
  status_key: '',
  subject_template: '',
  body_template: '',
  variable_schema_text: '',
  is_active: true,
};

const CommunicationTemplateManager = () => {
  const [templates, setTemplates] = useState([]);
  const [availableVariables, setAvailableVariables] = useState([]);
  const [defaultStatusKeys, setDefaultStatusKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const [confirmDialog, setConfirmDialog] = useState({
    show: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    onConfirm: null,
  });

  const [channelFilter, setChannelFilter] = useState('all');
  const [statusSearch, setStatusSearch] = useState('');

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const [templatesRes, variablesRes] = await Promise.all([
        axios.get('/api/communication-templates'),
        axios.get('/api/communication-templates/variables'),
      ]);
      setTemplates(Array.isArray(templatesRes.data) ? templatesRes.data : []);
      setAvailableVariables(Array.isArray(variablesRes.data?.order_status_variables) ? variablesRes.data.order_status_variables : []);
      setDefaultStatusKeys(Array.isArray(variablesRes.data?.default_status_keys) ? variablesRes.data.default_status_keys : []);
    } catch (loadErr) {
      setError(loadErr.response?.data?.detail || 'Failed to load communication templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredTemplates = useMemo(() => {
    const normalizedStatusSearch = statusSearch.trim().toLowerCase();
    return templates.filter((template) => {
      if (channelFilter !== 'all' && template.channel !== channelFilter) {
        return false;
      }
      if (normalizedStatusSearch && !String(template.status_key || '').toLowerCase().includes(normalizedStatusSearch)) {
        return false;
      }
      return true;
    });
  }, [templates, channelFilter, statusSearch]);

  const openCreateModal = () => {
    setEditingTemplate(null);
    setFormData(initialForm);
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const openEditModal = (template) => {
    setEditingTemplate(template);
    setFormData({
      template_name: template.template_name || '',
      channel: template.channel || 'email',
      status_key: template.status_key || '',
      subject_template: template.subject_template || '',
      body_template: template.body_template || '',
      variable_schema_text: Array.isArray(template.variable_schema) ? template.variable_schema.join(', ') : '',
      is_active: template.is_active !== false,
    });
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setEditingTemplate(null);
    setFormData(initialForm);
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const parseVariableSchema = (value) => value
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        template_name: formData.template_name.trim(),
        channel: formData.channel,
        status_key: formData.status_key.trim(),
        subject_template: formData.subject_template.trim() || null,
        body_template: formData.body_template.trim() || null,
        variable_schema: parseVariableSchema(formData.variable_schema_text),
        is_active: formData.is_active,
      };

      if (editingTemplate) {
        await axios.put(`/api/communication-templates/${editingTemplate.id}`, payload);
        setSuccess('Template updated successfully.');
      } else {
        await axios.post('/api/communication-templates', payload);
        setSuccess('Template created successfully.');
      }

      closeModal();
      await loadData();
    } catch (saveErr) {
      setError(saveErr.response?.data?.detail || 'Failed to save communication template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (template) => {
    setConfirmDialog({
      show: true,
      title: 'Delete Template',
      message: `Delete template "${template.template_name}"?`,
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          setError('');
          setSuccess('');
          await axios.delete(`/api/communication-templates/${template.id}`);
          setSuccess('Template deleted successfully.');
          await loadData();
        } catch (deleteErr) {
          setError(deleteErr.response?.data?.detail || 'Failed to delete communication template');
        }
      },
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ show: false, title: '', message: '', confirmText: 'Confirm', onConfirm: null });
  };

  const executeConfirmDialogAction = async () => {
    const action = confirmDialog.onConfirm;
    closeConfirmDialog();
    if (typeof action === 'function') {
      await action();
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status" />
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Row className="mb-3 align-items-center">
        <Col>
          <h1>Communication Templates</h1>
          <p className="text-muted mb-0">Configure order-status templates for Email, SMS, and WhatsApp with variable placeholders.</p>
        </Col>
        <Col xs="auto">
          <Button onClick={openCreateModal}>+ New Template</Button>
        </Col>
      </Row>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      <Card className="mb-3">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Channel</Form.Label>
                <Form.Select value={channelFilter} onChange={(event) => setChannelFilter(event.target.value)}>
                  <option value="all">All</option>
                  <option value="email">Email</option>
                  <option value="sms">SMS</option>
                  <option value="whatsapp">WhatsApp</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={5}>
              <Form.Group>
                <Form.Label>Status Key</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Search status key (e.g. order_status_shipped)"
                  value={statusSearch}
                  onChange={(event) => setStatusSearch(event.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3} className="text-md-end">
              <div className="small text-muted">Showing {filteredTemplates.length} of {templates.length}</div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Body>
          <h6 className="mb-2">Suggested Status Keys</h6>
          <div className="d-flex flex-wrap gap-2">
            {defaultStatusKeys.length === 0 ? (
              <span className="text-muted small">No default status keys found.</span>
            ) : defaultStatusKeys.map((statusKey) => (
              <Badge key={statusKey} bg="light" text="dark">{statusKey}</Badge>
            ))}
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body className="p-0">
          {filteredTemplates.length === 0 ? (
            <div className="p-4 text-center text-muted">No templates found for the current filters.</div>
          ) : (
            <Table responsive hover className="mb-0">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Channel</th>
                  <th>Status Key</th>
                  <th>Variables</th>
                  <th>Active</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTemplates.map((template) => (
                  <tr key={template.id}>
                    <td>{template.template_name}</td>
                    <td className="text-capitalize">{template.channel}</td>
                    <td><code>{template.status_key}</code></td>
                    <td>
                      {Array.isArray(template.variable_schema) && template.variable_schema.length > 0
                        ? template.variable_schema.join(', ')
                        : '-'}
                    </td>
                    <td>
                      <Badge bg={template.is_active ? 'success' : 'secondary'}>
                        {template.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <Button size="sm" variant="outline-primary" onClick={() => openEditModal(template)}>Edit</Button>
                        <Button size="sm" variant="outline-danger" onClick={() => handleDelete(template)}>Delete</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={closeModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingTemplate ? 'Edit Template' : 'Create Template'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Template Name</Form.Label>
                  <Form.Control
                    name="template_name"
                    value={formData.template_name}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Channel</Form.Label>
                  <Form.Select
                    name="channel"
                    value={formData.channel}
                    onChange={handleChange}
                    disabled={Boolean(editingTemplate)}
                  >
                    <option value="email">Email</option>
                    <option value="sms">SMS</option>
                    <option value="whatsapp">WhatsApp</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Status Key</Form.Label>
                  <Form.Control
                    name="status_key"
                    value={formData.status_key}
                    onChange={handleChange}
                    placeholder="order_status_shipped"
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label>Subject Template (email only)</Form.Label>
                  <Form.Control
                    name="subject_template"
                    value={formData.subject_template}
                    onChange={handleChange}
                    placeholder="Order {{order_id}} is now {{order_status}}"
                  />
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label>Body Template {formData.channel === 'email' ? '(required for email)' : '(optional)'}</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    name="body_template"
                    value={formData.body_template}
                    onChange={handleChange}
                    placeholder={formData.channel === 'email'
                      ? 'Hi {{buyer_name}}, your order {{order_id}} is now {{order_status}}.'
                      : 'Optional message template'}
                    required={formData.channel === 'email'}
                  />
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label>Variable Schema (comma separated)</Form.Label>
                  <Form.Control
                    name="variable_schema_text"
                    value={formData.variable_schema_text}
                    onChange={handleChange}
                    placeholder="order_id, buyer_name, order_status"
                  />
                  <Form.Text className="text-muted">
                    For SMS/WhatsApp, define variables expected by your API payload mapping.
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Check
                  type="switch"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  label="Template active"
                />
              </Col>
            </Row>

            <hr />
            <h6 className="mb-2">Available Variables</h6>
            <div className="d-flex flex-wrap gap-2">
              {availableVariables.length === 0 ? (
                <span className="text-muted small">No variables available</span>
              ) : availableVariables.map((name) => (
                <Badge key={name} bg="light" text="dark">{`{{${name}}}`}</Badge>
              ))}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={closeModal}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Saving...' : (editingTemplate ? 'Update' : 'Create')}</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={confirmDialog.show} onHide={closeConfirmDialog} centered>
        <Modal.Header closeButton>
          <Modal.Title>{confirmDialog.title || 'Confirm Action'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{confirmDialog.message}</Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeConfirmDialog}>Cancel</Button>
          <Button variant="danger" onClick={executeConfirmDialogAction}>
            {confirmDialog.confirmText || 'Confirm'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CommunicationTemplateManager;
